// src/pages/Home.js
// Page d'accueil Fynho facon Airbnb : "Consultees recemment" en haut, puis sections
// dynamiques SANS doublons. Code defensif partout : aucune donnee manquante ne peut
// casser le rendu (la page ne devient jamais blanche a cause des donnees).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { WifiOff, HomeIcon, Star, Building2, Home as HouseIcon, Globe2, Bell, Map as MapIcon } from 'lucide-react';
import Logo from '../components/shared/Logo';
import CategoryBar from '../components/home/CategoryBar';
import SearchBar from '../components/home/SearchBar';
import GeoButton from '../components/home/GeoButton';
import ListingRow from '../components/listing/ListingRow';
import HomeSkeleton from '../components/home/HomeSkeleton';
import LanguageSwitcher from '../components/shared/LanguageSwitcher';
import ThemeToggle from '../components/shared/ThemeToggle';
import { useTheme } from '../hooks/useTheme';
import apiClient from '../api/client';
import { generateOrganizationSchema } from '../utils/seo';
import ROUTES from '../constants/routes';
import GetAppButton from '../components/shared/GetAppButton';
import { preloadImages } from '../utils/preloadImages';

const RECENT_KEY = 'fynho_recent_listings';

// Lit les IDs des annonces consultees recemment. Ne jette jamais d'erreur.
function getRecentlyViewedIds() {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.map((x) => String(x)) : [];
  } catch (e) {
    return [];
  }
}

// Score de popularite tolerant aux champs manquants
function popularityScore(listing) {
  if (!listing) return 0;
  const views = parseInt(listing.views || listing.view_count || 0, 10);
  const rating = parseFloat(listing.rating || listing.average_rating || 0);
  return (isNaN(views) ? 0 : views) + (isNaN(rating) ? 0 : rating) * 50;
}

function cityOf(listing) {
  return ((listing && listing.city) || '').toString().toLowerCase();
}

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [allListings, setAllListings] = useState([]);
  const [sponsoredListings, setSponsoredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadListings = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const [allResult, sponsoredResult] = await Promise.all([
        apiClient.get('/endpoints/listings.php?action=list&status=published&limit=80'),
        apiClient.get('/endpoints/listings.php?action=list&status=published&sponsored=1&limit=10'),
      ]);
      const all = (allResult && Array.isArray(allResult.listings)) ? allResult.listings : [];
      const sponsored = (sponsoredResult && Array.isArray(sponsoredResult.listings)) ? sponsoredResult.listings : [];
      setAllListings(all);
      setSponsoredListings(sponsored);

      try {
        const allImageUrls = [...sponsored, ...all]
          .map((l) => (l && l.images && l.images[0]) ? l.images[0] : null)
          .filter(Boolean)
          .map((path) => (path.startsWith('http') ? path : 'https://fynho.com' + path));
        preloadImages(allImageUrls);
      } catch (e) { /* le preload ne doit jamais casser la page */ }
    } catch (err) {
      setError(true);
      setAllListings([]);
      setSponsoredListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  // ---------------- Sections sans doublons ----------------
  // Chaque annonce n'apparait qu'une fois. "used" memorise ce qui a deja ete affiche.
  const buildSections = () => {
    const list = Array.isArray(allListings) ? allListings : [];
    const sponsoredList = Array.isArray(sponsoredListings) ? sponsoredListings : [];
    const used = new Set();

    const take = (candidates, limit, minToShow) => {
      const min = minToShow || 1;
      const picked = [];
      const source = Array.isArray(candidates) ? candidates : [];
      for (let i = 0; i < source.length; i++) {
        const l = source[i];
        if (!l || l.id === undefined || l.id === null) continue;
        if (picked.length >= limit) break;
        const id = String(l.id);
        if (used.has(id)) continue;
        picked.push(l);
      }
      if (picked.length < min) return [];
      picked.forEach((l) => used.add(String(l.id)));
      return picked;
    };

    // 1) Consultees recemment - EN HAUT comme Airbnb
    const recentIds = getRecentlyViewedIds();
    const byId = new Map(list.filter((l) => l && l.id !== undefined).map((l) => [String(l.id), l]));
    const recentCandidates = recentIds.map((id) => byId.get(id)).filter(Boolean);
    const recent = take(recentCandidates, 10, 1);

    // 2) Coup de coeur (sponsorisees)
    const sponsored = take(sponsoredList, 10, 1);

    // 3) Populaires par ville (min 2 pour eviter une section a 1 annonce recyclee)
    const byPopularity = list.slice().sort((a, b) => popularityScore(b) - popularityScore(a));
    const popularDouala = take(byPopularity.filter((l) => cityOf(l) === 'douala'), 10, 2);
    const popularYaounde = take(byPopularity.filter((l) => cityOf(l) === 'yaounde' || cityOf(l) === 'yaoundé'), 10, 2);

    // 4) Nouveautes
    const byDate = list.slice().sort((a, b) => {
      const da = new Date((a && (a.created_at || a.createdAt)) || 0).getTime();
      const db = new Date((b && (b.created_at || b.createdAt)) || 0).getTime();
      return db - da;
    });
    const nouveautes = take(byDate, 10, 2);

    // 5) Par categorie (recoit le reste)
    const sejours = take(list.filter((l) => l && l.category === 'sejours'), 12, 1);
    const locations = take(list.filter((l) => l && l.category === 'locations'), 12, 1);
    const immobilier = take(list.filter((l) => l && l.category === 'immobilier'), 12, 1);
    const services = take(list.filter((l) => l && l.category === 'services'), 12, 1);

    return { recent, sponsored, popularDouala, popularYaounde, nouveautes, sejours, locations, immobilier, services };
  };

  const sections = buildSections();

  let organizationSchema = null;
  try { organizationSchema = generateOrganizationSchema(); } catch (e) { organizationSchema = null; }

  const styles = {
    container: { display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.bg },
    header: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px',
      position: 'sticky', top: 0, backgroundColor: theme.bg, zIndex: 50, borderBottom: '1px solid ' + theme.borderSoft,
    },
    headerControls: { display: 'flex', alignItems: 'center', gap: '8px' },
    rowsContainer: { paddingTop: '16px' },
    stateContainer: {
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '56px 24px', textAlign: 'center', gap: '14px', animation: 'fadeInSoft 300ms ease-out',
    },
    stateIconCircle: {
      width: '64px', height: '64px', borderRadius: '50%', backgroundColor: theme.bgCard,
      border: '1px solid ' + theme.border, display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    stateTitle: { fontSize: '16px', fontWeight: '700', color: theme.textPrimary },
    stateText: { fontSize: '14px', color: theme.textSecondary, maxWidth: '280px', lineHeight: '1.5' },
    retryButton: {
      marginTop: '4px', padding: '11px 26px', backgroundColor: theme.gold, border: 'none',
      borderRadius: '10px', color: '#FFFFFF', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
      transition: 'transform 150ms ease', boxShadow: '0 4px 12px ' + theme.gold + '40',
    },
    fadeInContent: { animation: 'fadeInSoft 400ms ease-out' },
    footer: { padding: '32px 16px 24px', textAlign: 'center', borderTop: '1px solid ' + theme.borderSoft },
    footerText: { fontSize: '12px', color: theme.textMuted, fontWeight: '500' },
    footerYear: { fontSize: '11px', color: theme.textMuted, marginTop: '8px' },
    mapButton: {
      position: 'fixed', bottom: '84px', left: '50%', transform: 'translateX(-50%)', display: 'flex',
      alignItems: 'center', gap: '8px', padding: '12px 22px', backgroundColor: theme.textPrimary,
      color: theme.bg, border: '1px solid ' + theme.gold, borderRadius: '24px', fontSize: '14px', fontWeight: '700',
      cursor: 'pointer', zIndex: 95, animation: 'mapGlowPulse 2.2s ease-in-out infinite',
    },
  };

  if (loading) {
    return <HomeSkeleton />;
  }

  const hasAnyListing = Array.isArray(allListings) && allListings.length > 0;

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeInSoft {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes mapGlowPulse {
          0%   { box-shadow: 0 4px 14px rgba(0,0,0,0.25), 0 0 0 0 ${theme.gold}66; }
          50%  { box-shadow: 0 4px 20px rgba(0,0,0,0.3), 0 0 22px 6px ${theme.gold}88; }
          100% { box-shadow: 0 4px 14px rgba(0,0,0,0.25), 0 0 0 0 ${theme.gold}66; }
        }
      `}</style>

      <Helmet>
        <title>Fynho - Find Your New Home | Immobilier au Cameroun</title>
        <meta name="description" content="Trouvez votre logement ideal au Cameroun : sejours, locations, terrains et services a Douala, Yaounde et plus." />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Fynho - Find Your New Home" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://fynho.com/" />
        <link rel="canonical" href="https://fynho.com/" />
        {organizationSchema && <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>}
      </Helmet>

      <div style={styles.header}>
        <Logo size="medium" showText={true} />
        <div style={styles.headerControls}>
          <ThemeToggle compact />
          <LanguageSwitcher compact />
        </div>
      </div>
      <GetAppButton />

      <SearchBar />
      <GeoButton />
      <CategoryBar />

      {error && (
        <div style={styles.stateContainer}>
          <div style={styles.stateIconCircle}>
            <WifiOff size={28} color={theme.textMuted} />
          </div>
          <div style={styles.stateTitle}>Connexion impossible</div>
          <div style={styles.stateText}>
            Nous n'avons pas pu charger les annonces. Verifie ta connexion et reessaie.
          </div>
          <button style={styles.retryButton} onClick={loadListings}>Reessayer</button>
        </div>
      )}

      {!error && !hasAnyListing && (
        <div style={styles.stateContainer}>
          <div style={styles.stateIconCircle}>
            <HomeIcon size={28} color={theme.gold} />
          </div>
          <div style={styles.stateTitle}>Aucune annonce pour le moment</div>
          <div style={styles.stateText}>Les premieres annonces publiees apparaitront ici tres bientot.</div>
        </div>
      )}

      {!error && hasAnyListing && (
        <div style={{ ...styles.rowsContainer, ...styles.fadeInContent }}>
          {sections.recent.length > 0 && (
            <ListingRow icon={<Star size={17} color={theme.gold} />} title="Consultees recemment" description="Reprenez la ou vous vous etiez arrete" listings={sections.recent} />
          )}

          {sections.sponsored.length > 0 && (
            <ListingRow icon={<Star size={17} fill={theme.gold} color={theme.gold} />} title={t('sections.premiumTitle')} description={t('sections.premiumDesc')} listings={sections.sponsored} />
          )}

          {sections.popularDouala.length > 0 && (
            <ListingRow icon={<Star size={17} color={theme.gold} />} title="Populaires a Douala" description="Les annonces les plus consultees a Douala" listings={sections.popularDouala} />
          )}

          {sections.popularYaounde.length > 0 && (
            <ListingRow icon={<Star size={17} color={theme.gold} />} title="Populaires a Yaounde" description="Les annonces les plus consultees a Yaounde" listings={sections.popularYaounde} />
          )}

          {sections.nouveautes.length > 0 && (
            <ListingRow icon={<Bell size={17} color={theme.gold} />} title="Nouveautes" description="Les dernieres annonces publiees" listings={sections.nouveautes} />
          )}

          {sections.sejours.length > 0 && (
            <ListingRow icon={<Building2 size={17} color={theme.gold} />} title={t('sections.sejoursTitle')} description={t('sections.sejoursDesc')} listings={sections.sejours} seeAllRoute={ROUTES.SEJOURS} />
          )}
          {sections.locations.length > 0 && (
            <ListingRow icon={<HouseIcon size={17} color={theme.gold} />} title={t('sections.locationsTitle')} description={t('sections.locationsDesc')} listings={sections.locations} seeAllRoute={ROUTES.LOCATIONS} />
          )}
          {sections.immobilier.length > 0 && (
            <ListingRow icon={<Globe2 size={17} color={theme.gold} />} title={t('sections.immobilierTitle')} description={t('sections.immobilierDesc')} listings={sections.immobilier} seeAllRoute={ROUTES.IMMOBILIER} />
          )}
          {sections.services.length > 0 && (
            <ListingRow icon={<Bell size={17} color={theme.gold} />} title={t('sections.servicesTitle')} description={t('sections.servicesDesc')} listings={sections.services} seeAllRoute={ROUTES.SERVICES} />
          )}
        </div>
      )}

      <div style={styles.footer}>
        <div style={styles.footerText}>Fynho</div>
        <div style={styles.footerYear}>&copy; {new Date().getFullYear()}</div>
      </div>

      <button style={styles.mapButton} onClick={() => navigate(ROUTES.MAP_SEARCH)}>
        <MapIcon size={16} /> {t('home.viewMap')}
      </button>
    </div>
  );
};

export default Home;